/**
 * Générateur — Il calcule avec les nombres rationnels : addition, soustraction,
 * multiplication, division.
 * Attendu ID : 147 | Niveau : 4ème | Domaine : Nombres (desc 11, O12)
 *
 * ⚠️ Doc prof MUET pour O12 (aucun exercice texte ni image dans le PDF) → BANQUE
 * CRÉÉE (décision de session, comme O7/O9/O18/O19). Plan de cartes validé
 * 2026-07-14 :
 *
 *   Carte « somme »      — a/b ± c/d, un entier parfois mêlé → fraction irréductible (2 trous num/den)
 *   Carte « produit »    — a/b × c/d ou a/b ÷ c/d, un entier parfois mêlé (2 trous)
 *   Carte « priorites »  — a/b + c/d × e/f (priorité du ×) (2 trous)
 *   Carte « completer »  — choisir le résultat en fraction irréductible (QCM)
 *
 * Revue 2026-09-22 (mesures sur dix mille tirages par carte) : aucune carte
 * supprimée, les maths étaient justes, les nombres trop gros.
 *   - Fractions tirées : numérateur 1..9 (avant 1..27 ; 29,7 % des numérateurs
 *     dépassaient 9, les dénominateurs de résultat montaient à 208 :
 *     « 26/9 + 25/8 = 433/72 » n'est pas du calcul de 4e).
 *   - « priorites » : fractions strictement inférieures à 1, dénominateurs 2..7
 *     (16,9 % des résultats avaient un dénominateur > 100, jusqu'à 648).
 *   - « somme » et « produit » : un opérande entier une fois sur trois (l'exemple
 *     officiel contient 5 + 3/4 et 5 ÷ 1/2) ; 1..9 en somme, 2..9 en produit
 *     (× 1 ou ÷ 1 viderait l'exercice : mode auto, À ARBITRER).
 *   - « completer » QCM : dans 47 % des tirages, trois options sur quatre
 *     portaient le dénominateur de la bonne réponse. Les options sont désormais
 *     la bonne réponse, une erreur à dénominateur faux (numérateurs et
 *     dénominateurs additionnés ou soustraits ; division au lieu du produit),
 *     le résultat NON RÉDUIT quand il existe (oubli de simplifier : même
 *     valeur sous une autre écriture, c'est la question posée) sinon une
 *     seconde erreur à dénominateur faux, et l'erreur de signe. Les voisins
 *     « ±1/den » restent en réserve. Consigne « en fraction irréductible ».
 *   - Lisibilité : tout opérande négatif est entre parenthèses, les opérateurs
 *     sont espacés ; la parenthèse explicative de « priorites » est une phrase.
 *   - Les consignes des trous précisent « dénominateur positif » (un résultat
 *     négatif -3/10 pourrait sinon être saisi 3 et -10 et compté faux).
 *
 * Arithmétique EXACTE (toolkit rationnel de nombres.js). Résultat toujours
 * irréductible avec dénominateur > 1 par construction (2 trous num/den ont donc
 * toujours un sens). Réponses saisies au moins ASCII « - ».
 *
 * Revue 2026-09-23 (typographie, règle prof) : les fractions affichées passent
 * en KaTeX, empilées (helpers tex/fracTrouTex de generators/utils.js). Les
 * trois phrases à trous deviennent une ligne KaTeX globale (entière entre $…$,
 * courte : le calcul puis « = » et la fraction attendue avec ses deux trous en
 * triple accolade) ; les énoncés et les options du QCM « completer » suivent.
 * Le résultat non réduit reste une option distincte de la bonne réponse
 * (« 6/8 » et « 3/4 » : deux écritures, deux chaînes). Un opérande négatif
 * garde ses parenthèses (\left( \right) autour d'une fraction) ; une option
 * négative est groupée entre accolades, sinon le \mathrm{} posé en tête par
 * tex() fait du moins un opérateur binaire espacé. Les paramètres du harnais
 * (_meta.params) gardent l'écriture ASCII de fmtFrac.
 */

import { randomChoice, randInt, makeTexteTrou, makeQcm, tex, fracTrouTex } from '../../../utils';
import { rat, ratAdd, ratSub, ratMul, ratDiv, ratEq, fmtFrac, pgcd } from './nombres';

const ATTENDU_ID = 147;

const L_SOMME = 'Additionner ou soustraire deux fractions';
const L_PRODUIT = 'Multiplier ou diviser deux fractions';
const L_PRIORITES = 'Calculer une expression fractionnaire (priorités)';
const L_COMPLETER = 'Reconnaître le résultat réduit d\'un calcul';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (demande prof 2026-09-23), IMPÉRATIF et sur les QUATRE cartes.
// L'intitulé dit « mental, à la main ou instrumenté », et aucune des quatre ne se
// fait de tête : « somme » et « completer » demandent un dénominateur commun,
// « priorites » deux étapes, « produit » une réduction. Un élève qui croit devoir
// répondre de tête répond faux, ou au hasard sur le QCM. La règle se lit par
// ATTENDU et non par carte : 147 se fait à la main et porte la phrase partout,
// 146 est construit pour le calcul mental et ne la porte nulle part. Forme maison
// des rappels du parc (6e 613 à 619, 5e 1044, 1045, 1061, 4e 143 et 149 : ligne
// grise en italique) ; tournure impérative, et non le « tu peux » permissif de
// 143, parce qu'ici poser ses calculs n'est pas une tolérance, c'est le geste.
// Elle ne souffle aucune méthode : ni dénominateur commun, ni inverse.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

// Fraction réduite non entière, numérateur 1..9, dénominateur 2..9 (revue
// 2026-09-22 : avant, numérateur jusqu'à 3 × dénominateur). signed=true → signe éventuel.
const properFrac = (signed = false) => {
    for (let i = 0; i < 80; i++) {
        const q = randInt(2, 9);
        const p = randInt(1, 9);
        if (p % q === 0) continue;                     // exclut les entiers
        const s = signed ? randomChoice([1, -1]) : 1;
        return rat(s * p, q);
    }
    return rat(signed ? -1 : 1, 3);
};
// Fraction réduite strictement inférieure à 1, dénominateur 2..qMax (carte « priorites »).
const fracInf1 = (qMax) => { const q = randInt(2, qMax); return rat(randInt(1, q - 1), q); };
// Entier de nMin à 9, signé ou non.
const entier = (nMin, signed) => rat((signed ? randomChoice([1, -1]) : 1) * randInt(nMin, 9), 1);
// Une fois sur trois, un entier remplace l'un des deux opérandes (revue 2026-09-22).
const avecEntier = () => randInt(1, 3) === 1;

// Affichage (revue 2026-09-23) : fragments LaTeX, composés dans tex() pour un
// énoncé ou dans la ligne KaTeX globale d'une phrase à trous. Fraction
// empilée, signe devant la barre ; un opérande négatif entre parenthèses.
const frag = (r) => {                                                  // « -\dfrac{3}{4} », « -5 »
    const s = r.p < 0 ? '-' : '';
    const a = Math.abs(r.p);
    return r.q === 1 ? `${s}${a}` : `${s}\\dfrac{${a}}{${r.q}}`;
};
const fragPar = (r) => {                                               // « \left(-\dfrac{3}{4}\right) », « (-5) »
    if (r.p >= 0) return frag(r);
    return r.q === 1 ? `(${frag(r)})` : `\\left(${frag(r)}\\right)`;
};
// Opérateur tiré (gardé tel quel dans les paramètres) → LaTeX.
const OP_TEX = { '+': '+', '−': '-', '×': '\\times', '÷': '\\div' };
// Une fraction seule en $…$ (option de QCM) ; un fragment négatif est groupé.
const K = (r) => { const f = frag(r); return tex(r.p < 0 ? `{${f}}` : f); };
// Calcul affiché : « a op b » en LaTeX, sans les $.
const calcul = (A, op, B) => `${fragPar(A)} ${OP_TEX[op]} ${fragPar(B)}`;

// ═════════ Carte « somme » — a/b ± c/d (2 trous) ═════════

const genSomme = () => {
    let A; let B; let op; let res;
    let secu = 0;
    do {
        A = properFrac(false);
        B = properFrac(false);
        if (avecEntier()) { if (randomChoice([true, false])) A = entier(1, false); else B = entier(1, false); }
        op = randomChoice(['+', '−']);
        res = op === '+' ? ratAdd(A, B) : ratSub(A, B);
    } while ((res.q === 1 || res.p === 0) && secu++ < 100);
    if (res.q === 1 || res.p === 0) { A = rat(1, 2); B = rat(1, 3); op = '+'; res = ratAdd(A, B); }   // repli déterministe
    const phrase = `$${calcul(A, op, B)} = ${fracTrouTex(`{{${res.p}}}`, `{{${res.q}}}`)}$`;
    const payload = makeTexteTrou(phrase,
        RAPPEL_BROUILLON
        + '<p>Calcule et donne le résultat en <b>fraction irréductible</b>, avec un <b>dénominateur positif</b>.</p>'
        + `<p>Écris d'abord le numérateur, puis le dénominateur : ${tex(`${calcul(A, op, B)} = \\ldots`)}</p>`,
        { ...opts(L_SOMME), mode: 'saisie' });
    payload._meta.params = { A: fmtFrac(A), B: fmtFrac(B), op, res: fmtFrac(res) };
    return payload;
};

// ═════════ Carte « produit » — a/b × c/d ou ÷ (2 trous) ═════════

const genProduit = () => {
    let A; let B; let op; let res;
    let secu = 0;
    do {
        A = properFrac(true);
        B = properFrac(true);
        if (avecEntier()) { if (randomChoice([true, false])) A = entier(2, true); else B = entier(2, true); }
        op = randomChoice(['×', '÷']);
        res = op === '×' ? ratMul(A, B) : ratDiv(A, B);
    } while ((res.q === 1 || res.p === 0) && secu++ < 100);
    if (res.q === 1 || res.p === 0) { A = rat(-3, 4); B = rat(2, 5); op = '×'; res = ratMul(A, B); }   // repli déterministe
    const phrase = `$${calcul(A, op, B)} = ${fracTrouTex(`{{${res.p}}}`, `{{${res.q}}}`)}$`;
    const payload = makeTexteTrou(phrase,
        RAPPEL_BROUILLON
        + '<p>Calcule et donne le résultat en <b>fraction irréductible</b>, avec un <b>dénominateur positif</b>.</p>'
        + `<p>Numérateur puis dénominateur : ${tex(`${calcul(A, op, B)} = \\ldots`)}</p>`,
        { ...opts(L_PRODUIT), mode: 'saisie' });
    payload._meta.params = { A: fmtFrac(A), B: fmtFrac(B), op, res: fmtFrac(res) };
    return payload;
};

// ═════════ Carte « priorites » — a/b + c/d × e/f (2 trous) ═════════

const genPriorites = () => {
    let A; let B; let C; let res;
    let secu = 0;
    do {
        A = fracInf1(7);
        B = fracInf1(7);
        C = fracInf1(7);
        res = ratAdd(A, ratMul(B, C));                 // priorité du ×
    } while ((res.q === 1 || res.p === 0) && secu++ < 100);
    if (res.q === 1 || res.p === 0) { A = rat(1, 2); B = rat(2, 3); C = rat(3, 5); res = ratAdd(A, ratMul(B, C)); }   // = 9/10
    const expr = `${frag(A)} + ${frag(B)} \\times ${frag(C)}`;
    const phrase = `$${expr} = ${fracTrouTex(`{{${res.p}}}`, `{{${res.q}}}`)}$`;
    const payload = makeTexteTrou(phrase,
        RAPPEL_BROUILLON
        + '<p>Attention aux <b>priorités</b> : la multiplication passe avant l\'addition.</p>'
        + '<p>Donne le résultat en fraction irréductible, le numérateur puis le dénominateur : '
        + `${tex(`${expr} = \\ldots`)}</p>`,
        { ...opts(L_PRIORITES), mode: 'saisie' });
    payload._meta.params = { A: fmtFrac(A), B: fmtFrac(B), C: fmtFrac(C), res: fmtFrac(res) };
    return payload;
};

// L'article et le genre s'accordent au nom de l'opération : « LA somme », « LA
// différence », mais « LE produit ». L'ancienne consigne construisait la phrase
// autour du seul nom et affichait « Quelle est la produit » une fois sur trois
// (défaut vu au dépouillement du 2026-09-23). La phrase entière est donc écrite
// pour chaque opérateur, plutôt que recollée autour d'un article figé.
const QUESTION_OP = {
    '+': 'Quelle est la <b>somme</b>, en fraction irréductible ?',
    '−': 'Quelle est la <b>différence</b>, en fraction irréductible ?',
    '×': 'Quel est le <b>produit</b>, en fraction irréductible ?',
};

// ═════════ Carte « completer » — QCM du résultat en fraction irréductible ═════════

const genCompleterQcm = () => {
    let A; let B; let op; let res;
    let secu = 0;
    do {
        A = properFrac(true);
        B = properFrac(false);
        op = randomChoice(['+', '−', '×']);
        res = op === '+' ? ratAdd(A, B) : (op === '−' ? ratSub(A, B) : ratMul(A, B));
    } while ((res.q === 1 || res.p === 0) && secu++ < 100);
    if (res.q === 1 || res.p === 0) { A = rat(-3, 4); B = rat(2, 5); op = '+'; res = ratAdd(A, B); }   // repli déterministe

    // Options (revue 2026-09-22 : le dénominateur de la bonne réponse était
    // offert par les voisins) : la bonne réponse, une erreur à dénominateur
    // faux, le résultat non réduit s'il existe (sinon une seconde erreur à
    // dénominateur faux), l'erreur de signe ; voisins en réserve. Valeurs
    // distinctes entre elles ; seul le résultat non réduit a la valeur de la
    // bonne réponse, sous une autre écriture (la question demande la fraction
    // irréductible), et le scoring du QCM compare les lettres.
    const retenus = [res];
    const choix = [res];                                             // rationnels, affichés en KaTeX à la fin
    const pushVal = (r) => {
        if (!r || r.q <= 0 || r.p === 0 || retenus.some(c => ratEq(c, r))) return;
        retenus.push(r); choix.push(r);
    };
    const sg = op === '+' ? 1 : -1;
    // 1. l'erreur propre à l'opération, dénominateur faux
    if (op === '+') pushVal(rat(A.p + B.p, A.q + B.q));            // additionne numérateurs et dénominateurs
    if (op === '−') pushVal(rat(A.p - B.p, Math.abs(A.q - B.q) || (A.q + B.q)));
    if (op === '×') pushVal(ratDiv(A, B));                           // divise au lieu de multiplier (produit en croix)
    // 2. le résultat non réduit (oubli de simplifier), sinon un second dénominateur faux
    const L = (A.q * B.q) / pgcd(A.q, B.q);
    const brut = op === '×'
        ? { p: A.p * B.p, q: A.q * B.q }
        : { p: A.p * (L / A.q) + sg * B.p * (L / B.q), q: L };
    if (pgcd(brut.p, brut.q) > 1) choix.push(brut);                  // non réduit : même valeur, autre écriture
    else if (op === '×') pushVal(rat(A.p * B.p, A.q + B.q));         // dénominateurs additionnés au lieu de multipliés
    else pushVal(rat(A.p * B.q + sg * B.p * A.q, A.q + B.q));        // numérateurs convertis, dénominateurs additionnés
    // 3. erreur de signe
    pushVal({ p: -res.p, q: res.q });
    // 4. réserve : additionne au lieu de multiplier (×), voisins (±1/den), puis res + k
    if (op === '×') pushVal(rat(A.p * B.p + A.q * B.q, A.q * B.q));
    pushVal(ratAdd(res, rat(1, res.q)));
    pushVal(ratSub(res, rat(1, res.q)));
    for (let k = 2; choix.length < 4 && k < 10; k++) pushVal(ratAdd(res, rat(k, 1)));

    const options = choix.slice(0, 4).map(K);
    const payload = makeQcm(options, K(res),
        RAPPEL_BROUILLON
        + `<p>${QUESTION_OP[op]}</p>`
        + `<p><b>${tex(calcul(A, op, B))}</b></p>`,
        opts(L_COMPLETER));
    payload._meta.params = { A: fmtFrac(A), B: fmtFrac(B), op, correct: fmtFrac(res) };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    somme_texte_trou_saisie: genSomme,
    produit_texte_trou_saisie: genProduit,
    priorites_texte_trou_saisie: genPriorites,
    completer_qcm_unique: genCompleterQcm,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'somme', label: L_SOMME, etapes: 3, formatsList: buildFormats('somme') },
    { id: 'produit', label: L_PRODUIT, etapes: 3, formatsList: buildFormats('produit') },
    { id: 'priorites', label: L_PRIORITES, etapes: 3, formatsList: buildFormats('priorites') },
    { id: 'completer', label: L_COMPLETER, etapes: 1, formatsList: buildFormats('completer') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
